export const TOOLTIP_MAX_WIDTH = 300;

export const formatYAxisTickLabel = (
  value: number,
  minDomain: number,
  maxDomain: number,
  minYAxisValue: number,
): string => {
  if (value === minDomain && minDomain !== 0) {
    return minDomain <= minYAxisValue ? `<${minYAxisValue}` : String(minDomain);
  }

  return String(value);
};

export const maskMinimumValue = (
  value: number,
  minYAxisValue?: number,
): number | string => {
  if (!minYAxisValue) {
    return value;
  }

  if (value < minYAxisValue) {
    return `< ${minYAxisValue}`;
  }

  return value;
};

export const hasValue = (v: unknown): v is number | string =>
  v !== undefined && v !== null;

export const toFiniteNumber = (v: number | string): number | null => {
  const n = Number(v);

  return Number.isFinite(n) ? n : null;
};

export interface GapBridge {
  dataKey: string;
  seriesKey: string;
}

export interface GapBridgeData<T> {
  rows: T[];
  bridges: GapBridge[];
}

const gapBridgeKey = (seriesKey: string, gapStart: number): string =>
  `${seriesKey}__gapBridge${gapStart}`;

interface ReportedPoint {
  index: number;
  value: number;
}

const reportedPoints = <T>(data: T[], key: string): ReportedPoint[] =>
  data.flatMap((point, index) => {
    const raw = (point as Record<string, unknown>)[key];

    if (!hasValue(raw)) return [];

    const value = toFiniteNumber(raw);

    return value === null ? [] : [{ index, value }];
  });

export const buildGapBridges = <T>(
  data: T[],
  seriesKeys: string[],
): GapBridgeData<T> => {
  const valuesByKey = new Map<string, Array<number | null>>();
  const bridges: GapBridge[] = [];

  seriesKeys.forEach((seriesKey) => {
    const reported = reportedPoints(data, seriesKey);

    reported.forEach((from, position) => {
      const to = reported[position + 1];

      // Adjacent periods need no bridge: the solid line already joins them.
      if (!to || to.index === from.index + 1) return;

      const dataKey = gapBridgeKey(seriesKey, from.index);
      const values: Array<number | null> = data.map(() => null);
      const span = to.index - from.index;

      for (let step = 0; step <= span; step += 1) {
        values[from.index + step] =
          from.value + ((to.value - from.value) * step) / span;
      }

      valuesByKey.set(dataKey, values);
      bridges.push({ dataKey, seriesKey });
    });

    // Past its last reported period the source has nothing more to say, so the
    // last known value runs flat to the edge of the range
    const last = reported[reported.length - 1];
    const lastIndex = data.length - 1;

    if (!last || last.index === lastIndex) return;

    const dataKey = gapBridgeKey(seriesKey, last.index);

    valuesByKey.set(
      dataKey,
      data.map((_, index) => (index < last.index ? null : last.value)),
    );
    bridges.push({ dataKey, seriesKey });
  });

  if (bridges.length === 0) {
    return { rows: data, bridges };
  }

  const rows = data.map((point, index) => {
    const bridgeValues: Record<string, number | null> = {};

    valuesByKey.forEach((values, dataKey) => {
      bridgeValues[dataKey] = values[index] ?? null;
    });

    return { ...point, ...bridgeValues };
  });

  return { rows, bridges };
};
